import { TableNode } from "../models/TableNode";

export class TableSelectionManager {

    /** Nodos raíz filtrados (visibles) */
    private filteredRootNodes: TableNode[] = [];
    /** Nodos raíz completos (todos los nodos) */
    private itemsRootNodes: TableNode[] = [];

    // ======================================================================
    //#region INTERFAZ PÚBLICA
    // ======================================================================

    /**
     * Inyecta o actualiza el contexto de la tabla.
     * Se debe llamar cada vez que la tabla filtre datos o se reinicie.
     */
    public updateContext(
        filteredRoots: TableNode[],
        itemsRoots: TableNode[]
    ): void {
        this.filteredRootNodes = filteredRoots;
        this.itemsRootNodes = itemsRoots;
    }

    /**
     * Punto de entrada principal para checkear/descheckear uno o múltiples items.
     * @param items Items en los que el usuario hizo click (pueden ser de cualquier nivel).
     * @param isChecked El nuevo valor booleano.
     */
    public toggleItemsSelection(items: TableNode[], isChecked: boolean): boolean {
        const parentsToUpdate = new Set<TableNode>();
        let anyChange = false;

        // Primero propagamos la intención de selección hacia abajo, y vamos marcando qué nodos cambiaron
        items.forEach(item => {
            const hasChanged = this.cascadeDown(item, isChecked);
            if (hasChanged) {
                anyChange = true;
            }

            // Si el nodo cambió y tiene padre, ese padre necesita ser recalculado (subir burbujeando)
            if (hasChanged && item.itemParent) {
                parentsToUpdate.add(item.itemParent);
            }
        });

        // Para cada padre marcado, subir recalculando su estado exacto
        parentsToUpdate.forEach(parent => {
            this.bubbleUp(parent);
        });

        return anyChange;
    }

    /**
     * Checkea o descheckea TODOS los items filtrados (que estén habilitados).
     */
    public toggleSelectAll(isChecked: boolean): void {
        const nodes = isChecked
            ? this.filteredRootNodes
            : this.getCheckedNodes();
        this.toggleItemsSelection(nodes, isChecked);
    }

    public getCheckedNodes(): TableNode[] {
        return this.itemsRootNodes.filter(node => node.isChecked);
    }

    public getActiveSelectedNodes(): TableNode[] {
        return this.itemsRootNodes.filter(node => node.hasActiveSelection);
    }

    public getUnActiveSelectedFilteredNodes(): TableNode[] {
        return this.filteredRootNodes.filter(node => !node.hasActiveSelection);
    }

    public getIndeterminateNodes(): TableNode[] {
        return this.itemsRootNodes.filter(node => node.isIndeterminateChecked);
    }

    public getCheckedData(): any[] {
        return this.getCheckedNodes().map(node => node.data);
    }

    public getActiveSelectedData(): any[] {
        return this.getActiveSelectedNodes().map(node => node.data);
    }

    /** Obtiene los nodos disponibles para ser checkeados (habilitados y no checkeados). */
    public getAvailableCheckableNodes(): TableNode[] {
        return this.filteredRootNodes.filter(node => node.isRowEnable && !node.isChecked);
    }

    /** Obtiene los nodos disponibles para ser fijados (habilitados y no fijados). */
    public getAvailableUnpinnedNodes(): TableNode[] {
        return this.filteredRootNodes.filter(node => !node.hasActiveSelection && node.isRowEnable);
    }

    /** Verifica si hay nodos checkeados. */
    public hasCheckedNodes(): boolean {
        return this.itemsRootNodes.some(node => node.isChecked);
    }

    /** Verifica si hay nodos fijados (pinned). */
    public hasActiveSelectedNodes(): boolean {
        return this.itemsRootNodes.some(node => node.hasActiveSelection);
    }

    // ======================================================================
    //#region LÓGICA DE SELECCIÓN
    // ======================================================================

    /**
     * Baja por el árbol aplicando la selección al nodo objetivo y a todos sus descendientes.
     */
    private cascadeDown(node: TableNode, isChecked: boolean): boolean {
        if (isChecked && !node.isRowEnable) return false; // No se puede checkear un nodo deshabilitado

        let nodeChanged = false;

        // Si el nodo tiene hijos, primero propagamos hacia abajo
        if (node.childDataFilteredArray && node.childDataFilteredArray.length > 0) {
            node.childDataFilteredArray.forEach(child => {
                const childChanged = this.cascadeDown(child, isChecked);
                if (childChanged) {
                    nodeChanged = true;
                }
            });

            const selfChanged = node.syncCheckStateWithChildren();

            if (selfChanged) {
                nodeChanged = true;
            }
        }
        // Nodo hoja (no tiene hijos). Aplicar cambio directo
        else {
            if (node.isChecked !== isChecked || node.isIndeterminateChecked) {
                node.isChecked = isChecked;
                node.isIndeterminateChecked = false;
                nodeChanged = true;
            }
        }

        return nodeChanged;
    }

    /**
     * Sube por el árbol recalculando el estado exacto de cada ancestro.
     */
    private bubbleUp(node: TableNode): void {
        let current: TableNode | undefined | null = node;

        while (current) {
            const hasChanged = current.syncCheckStateWithChildren();

            if (!hasChanged) {
                break;
            }

            current = current.itemParent; // Subir nivel
        }
    }

    // /**
    //  * Centraliza el cambio de estado.
    //  * El mapa de checkeados SOLO almacena nodos de Nivel 1.
    //  */
    // private updateNodeState(node: TableNode, isChecked: boolean, isIndeterminate: boolean): void {
    //     node.isChecked = isChecked;
    //     node.isIndeterminateChecked = isIndeterminate;
    //     // const isLevel1 = node.levelRow === 1 || !node.itemParent; // es nivel 1 (raíz)
    //     // if (isLevel1) {
    //     //     if (isChecked) this.checkedMap.set(node.id, node);
    //     //     else  this.checkedMap.delete(node.id);
    //     // }
    // }
}